import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LoginForm from './components/LoginForm';
import HomePage from './components/HomePage';
import SettingsPage from './components/SettingsPage';
import MappingRegistrationList from './components/MappingRegistrationList';
import MappingRegistrationForm from './components/MappingRegistrationForm';
import BatchHistoryList from './components/BatchHistoryList';
import BatchHistoryDetail from './components/BatchHistoryDetail';
import ToastProvider from './components/ToastProvider';
import { authService, LoginRequest } from './services/authService';
import { useNavigation } from './hooks/useNavigation';
import './styles/colors.css';
import './App.css';

// Wrapper component to access location and handle redirects
const AppContent: React.FC<{
  isAuthenticated: boolean;
  user: any;
  handleLogin: (loginData: any) => Promise<void>;
  handleLogout: () => Promise<void>;
}> = ({ isAuthenticated, user, handleLogin, handleLogout }) => {
  const location = useLocation();
  
  // Initialize navigation service
  useNavigation();
  
  // Get the redirect URL from location state or search params
  const getRedirectUrl = () => {
    const state = location.state as any;
    if (state?.from) {
      return state.from;
    }
    
    // Check URL search params for redirect
    const searchParams = new URLSearchParams(location.search);
    const redirect = searchParams.get('redirect');
    if (redirect) {
      return decodeURIComponent(redirect);
    }
    
    return null;
  };

  const redirectUrl = getRedirectUrl();

  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          isAuthenticated ? (
            <Navigate to={redirectUrl || "/"} replace />
          ) : (
            <LoginForm onLogin={handleLogin} redirectUrl={redirectUrl} />
          )
        } 
      />
      <Route 
        path="/" 
        element={
          isAuthenticated ? (
            <HomePage user={user} onLogout={handleLogout} />
          ) : (
            <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />
          )
        } 
      />
      <Route 
        path="/settings" 
        element={
          isAuthenticated ? (
            <SettingsPage user={user} onLogout={handleLogout} />
          ) : (
            <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />
          )
        } 
      />
      <Route 
        path="/mapping_registration_list" 
        element={
          isAuthenticated ? (
            <MappingRegistrationList user={user} onLogout={handleLogout} />
          ) : (
            <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />
          )
        } 
      />
      <Route 
        path="/mapping_registration_list/new" 
        element={
          isAuthenticated ? (
            <MappingRegistrationForm user={user} onLogout={handleLogout} />
          ) : (
            <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />
          )
        } 
      />
      <Route 
        path="/mapping_registration_list/edit/:id" 
        element={
          isAuthenticated ? (
            <MappingRegistrationForm user={user} onLogout={handleLogout} />
          ) : (
            <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />
          )
        } 
      />
      <Route 
        path="/batch_history" 
        element={
          isAuthenticated ? (
            <BatchHistoryList user={user} onLogout={handleLogout} />
          ) : (
            <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />
          )
        } 
      />
      <Route 
        path="/batch_history/:id" 
        element={
          isAuthenticated ? (
            <BatchHistoryDetail user={user} onLogout={handleLogout} />
          ) : (
            <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />
          )
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);

  // // Token checker hook
  // useTokenChecker({
  //   checkInterval: 5 * 60 * 1000, // Check every 5 minutes
  //   onTokenRefreshed: () => {
  //     // Token refreshed successfully
  //   },
  //   onRefreshFailed: () => {
  //     console.log('Token refresh failed, logging out');
  //     handleLogout();
  //   }
  // });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const hasToken = authService.isAuthenticated();
        if (hasToken) {
          const userData = authService.getCurrentUser();
          setUser(userData);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Auth check error:', error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const handleLogin = async (loginData: LoginRequest) => {
    try {
      const response = await authService.login(loginData);
      if (response.success) {
        const userData = authService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        // errorCode を保持してスローし、LoginForm の catch で分岐できるようにする
        const err: any = new Error(response.message || 'Login failed');
        if (response.errorCode) err.errorCode = response.errorCode;
        throw err;
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>アプリケーションを読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="app">
        <ToastProvider />
        <AppContent 
          isAuthenticated={isAuthenticated}
          user={user}
          handleLogin={handleLogin}
          handleLogout={handleLogout}
        />
      </div>
    </Router>
  );
};

export default App;
